import React, { useState } from 'react';
import { Steps, Input, Select, Button, Switch, Modal, Form, DatePicker, Upload, InputNumber, Drawer } from 'antd';
import {
    Type, AlignLeft, Info, Hash, DollarSign, Calculator,
    ChevronDown, List as ListIcon, Calendar, CalendarRange, FileText, Folder,
    Copy, Edit3, Trash2, Clock, User, Inbox, Plus,
} from 'lucide-react';
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

import Layout from '../../Layout';
import DocumentSigningApplication from './DocumentSigningApplication';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Dragger } = Upload;

export default function CreateWorkflow() {
    const [currentStep, setCurrentStep] = useState(0);

    // States lưu dữ liệu
    const [addedFields, setAddedFields] = useState([]);
    const [approvalSteps, setApprovalSteps] = useState([]);

    // States Modal & Drawer
    const [isStandardModalOpen, setIsStandardModalOpen] = useState(false);
    const [isFileModalOpen, setIsFileModalOpen] = useState(false);
    const [isStepModalOpen, setIsStepModalOpen] = useState(false);
    const [isAddApproverModalOpen, setIsAddApproverModalOpen] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false); // <--- State bật tắt Sidebar Mobile

    // States Drag & Drop Nâng cao
    const [pendingField, setPendingField] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [insertIndex, setInsertIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const [tempApprovers, setTempApprovers] = useState([]);
    const [editingStepId, setEditingStepId] = useState(null);
    const [editingApproverIndex, setEditingApproverIndex] = useState(null);

    const [formStandard] = Form.useForm();
    const [formFile] = Form.useForm();
    const [formStep] = Form.useForm();
    const [formApprover] = Form.useForm();

    const sidebarGroups = [
        { title: 'Chữ', items: [{ id: 'short_text', name: 'Câu ngắn', icon: Type }, { id: 'long_text', name: 'Đoạn văn', icon: AlignLeft }, { id: 'description', name: 'Mô tả', icon: Info }] },
        { title: 'Số', items: [{ id: 'number', name: 'Số', icon: Hash }, { id: 'currency', name: 'Tiền', icon: DollarSign }, { id: 'calc', name: 'Tính toán', icon: Calculator }] },
        { title: 'Lựa chọn', items: [{ id: 'single_choice', name: 'Lựa chọn đơn', icon: ChevronDown }, { id: 'multi_choice', name: 'Lựa chọn kép', icon: ListIcon }] },
        { title: 'Ngày giờ', items: [{ id: 'date', name: 'Chọn ngày', icon: Calendar }, { id: 'date_range', name: 'Giới hạn ngày', icon: CalendarRange }] },
        { title: 'File', items: [{ id: 'file_sign', name: 'File ký', icon: FileText }, { id: 'file_attach', name: 'File đính kèm', icon: Folder }] }
    ];

    // ==========================================
    // LOGIC KÉO THẢ & CLICK (MOBILE)
    // ==========================================

    // PC: Nắm kéo
    const handleDragStartSidebar = (e, item) => e.dataTransfer.setData('newField', JSON.stringify(item));

    // Mobile/PC: Click/Tap thẳng vào item để thêm
    const handleSidebarItemClick = (item) => {
        setIsMobileSidebarOpen(false); // Đóng drawer trên mobile
        openConfigModal(item, null);   // Mở cấu hình
    };

    const handleDropZone = (e) => {
        e.preventDefault();
        setDragOverIndex(null);
        const fieldDataStr = e.dataTransfer.getData('newField');
        if (fieldDataStr) openConfigModal(JSON.parse(fieldDataStr), null);
    };

    const handleItemDrop = (e, targetIndex) => {
        e.preventDefault(); e.stopPropagation();
        setDragOverIndex(null);
        const dragIndex = e.dataTransfer.getData('sortIndex');
        const newFieldStr = e.dataTransfer.getData('newField');

        if (dragIndex !== "") {
            const newFields = [...addedFields];
            const [draggedItem] = newFields.splice(Number(dragIndex), 1);
            newFields.splice(targetIndex, 0, draggedItem);
            setAddedFields(newFields);
        } else if (newFieldStr) {
            openConfigModal(JSON.parse(newFieldStr), targetIndex);
        }
    };

    const openConfigModal = (fieldData, index) => {
        setPendingField(fieldData); setEditingId(null); setInsertIndex(index);
        if (fieldData.id.includes('file')) {
            formFile.resetFields(); setIsFileModalOpen(true);
        } else {
            formStandard.resetFields(); formStandard.setFieldsValue({ label: fieldData.name, required: false }); setIsStandardModalOpen(true);
        }
    };

    const saveNewFieldToList = (newField) => {
        if (editingId) setAddedFields(prev => prev.map(f => f.id === editingId ? newField : f));
        else setAddedFields(prev => {
            const newArr = [...prev];
            if (insertIndex !== null) newArr.splice(insertIndex, 0, newField);
            else newArr.push(newField);
            return newArr;
        });
        setInsertIndex(null);
    };

    const handleSaveStandardField = async () => {
        try {
            const values = await formStandard.validateFields();
            saveNewFieldToList({ id: editingId || `field_${Date.now()}`, type: pendingField.id, label: values.label, placeholder: values.placeholder, required: values.required });
            setIsStandardModalOpen(false);
        } catch (error) { }
    };

    const handleSaveFileField = async () => {
        try {
            const values = await formFile.validateFields();
            saveNewFieldToList({ id: editingId || `file_${Date.now()}`, type: pendingField.id, label: values.label, description: values.description, required: values.required, isFile: true });
            setIsFileModalOpen(false);
        } catch (error) { }
    };

    const handleEditStep = (step) => {
        setEditingStepId(step.id); formStep.setFieldsValue({ name: step.name, mode: step.mode || 'Tuần tự' });
        setTempApprovers([...(step.approvers || [])]); setIsStepModalOpen(true);
    };

    const handleOpenApproverModal = (index = null) => {
        setEditingApproverIndex(index);
        if (index !== null) formApprover.setFieldsValue(tempApprovers[index]);
        else formApprover.resetFields();
        setIsAddApproverModalOpen(true);
    };

    const handleSaveApprover = async () => {
        try {
            const values = await formApprover.validateFields();
            if (editingApproverIndex !== null) {
                const newApprovers = [...tempApprovers]; newApprovers[editingApproverIndex] = values; setTempApprovers(newApprovers);
            } else setTempApprovers([...tempApprovers, values]);
            setIsAddApproverModalOpen(false);
        } catch (error) { }
    };

    const renderInteractiveInput = (field) => {
        const commonProps = { placeholder: field.placeholder, className: "w-full", size: "large" };
        switch (field.type) {
            case 'short_text': return <Input {...commonProps} className={`${commonProps.className} dark:bg-gray-800 dark:border-gray-700 dark:text-white`} />;
            case 'long_text': return <TextArea {...commonProps} rows={4} className={`${commonProps.className} dark:bg-gray-800 dark:border-gray-700 dark:text-white`} />;
            case 'number': return <InputNumber {...commonProps} style={{ width: '100%' }} />;
            case 'currency': return <InputNumber {...commonProps} style={{ width: '100%' }} formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />;
            case 'date': return <DatePicker {...commonProps} style={{ width: '100%' }} format="DD/MM/YYYY" />;
            case 'date_range': return <RangePicker {...commonProps} style={{ width: '100%' }} format="DD/MM/YYYY" />;
            case 'single_choice': return <Select {...commonProps} style={{ width: '100%' }}><Option value="opt1">Tùy chọn 1</Option><Option value="opt2">Tùy chọn 2</Option></Select>;
            case 'file_sign':
            case 'file_attach':
                return (
                    <div className="border border-gray-300 dark:border-gray-700 rounded-md p-3 bg-gray-50 dark:bg-gray-900 flex items-center gap-3 w-full">
                        <Folder className="text-blue-500" size={20} />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-blue-600 text-000000e0">File đính kèm.pdf</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{field.description || 'Đã tải lên'}</p>
                        </div>
                    </div>
                );
            default: return <Input {...commonProps} className={`${commonProps.className} dark:bg-gray-800 dark:border-gray-700 dark:text-white`} />;
        }
    };

    // Thành phần Sidebar Render chung cho cả PC và Mobile
    const renderSidebarContent = () => (
        <div className="space-y-6 max-h-[70vh] lg:max-h-[70vh] overflow-y-auto custom-scrollbar pr-2">
            {sidebarGroups.map((group, gIdx) => (
                <div key={gIdx}>
                    <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-3">{group.title}</h4>
                    <div className="flex flex-col gap-2">
                        {group.items.map((item) => (
                            <div
                                key={item.id} draggable
                                onDragStart={(e) => handleDragStartSidebar(e, item)}
                                onClick={() => handleSidebarItemClick(item)} // Thêm sự kiện Click cho Mobile
                                className="flex items-center justify-between px-3 py-3 lg:py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-600 dark:text-gray-300 cursor-pointer lg:cursor-grab hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <span className="text-sm font-medium lg:font-normal">{item.name}</span>
                                <item.icon size={16} className="text-gray-400 dark:text-gray-500" />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <Layout>
            <div>
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm">
                    <Link to="/submit-for-signature" className="flex items-center gap-2">
                        <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                    </Link>
                    <Link to="/submit-for-signature" className="flex items-center gap-2 text-000000e0">
                        <span>Quản lý trình duyệt</span>
                    </Link>
                    <span><RightOutlined className="w-4 h-4 text-[12px] text-gray-500 text-000000e0" /></span>
                    <span className="text-000000e0">Tạo mới quy trình mẫu</span>
                </div>

                {/* Header Steps có fix màu text Dark Mode */}
                <div className="py-6 flex justify-center border-b border-gray-200 dark:border-gray-700 mb-6 transition-colors duration-300">
                    <Steps
                        current={currentStep}
                        className={`max-w-4xl 
                            dark:[&_.ant-steps-item-process_.ant-steps-item-title]:!text-white 
                            dark:[&_.ant-steps-item-wait_.ant-steps-item-title]:!text-gray-400 
                            dark:[&_.ant-steps-item-wait_.ant-steps-item-icon]:!border-gray-600 
                            dark:[&_.ant-steps-item-wait_.ant-steps-item-icon]:!bg-gray-800
                            dark:[&_.ant-steps-item-wait_.ant-steps-icon]:!text-gray-400
                            dark:[&_.ant-steps-item-finish_.ant-steps-item-title]:!text-gray-300
                            dark:[&_.ant-steps-item-tail::after]:!bg-gray-700
                        `}
                        items={[{ title: 'Khởi tạo' }, { title: 'Thiết lập vị trí ký' }, { title: 'Thiết lập trước khi gửi' }]}
                    />
                </div>

                {currentStep === 0 && (
                    <main className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">

                        {/* SIDEBAR DÀNH CHO PC (Ẩn trên màn hình nhỏ) */}
                        <div className="hidden lg:block lg:col-span-3">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sticky top-6">
                                <h3 className="font-semibold text-blue-600 border-b-2 border-blue-600 inline-block pb-2 mb-4">Thiết lập trường</h3>
                                {renderSidebarContent()}
                            </div>
                        </div>

                        {/* VÙNG THIẾT LẬP CHÍNH */}
                        <div className="lg:col-span-9">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-8 min-h-[70vh]">
                                <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4 text-base">Thông tin quy trình</h3>
                                <Select className="w-full mb-8" size="large" defaultValue="Ban CNTT">
                                    <Option value="Ban CNTT">Ban CNTT</Option>
                                    <Option value="Hành chính">Hành chính nhân sự</Option>
                                </Select>

                                {/* Header Vùng thiết lập + Nút thêm trường cho Mobile */}
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="font-semibold text-blue-600 text-sm">Vùng thiết lập</h4>

                                    {/* Nút này chỉ hiện trên Mobile/Tablet */}
                                    <Button
                                        type="primary"
                                        icon={<Plus size={16} />}
                                        className="lg:hidden bg-blue-600 flex items-center"
                                        onClick={() => setIsMobileSidebarOpen(true)}
                                    >
                                        Thêm trường
                                    </Button>
                                </div>

                                {/* CONTAINER VÙNG KÉO THẢ */}
                                <div className="pb-4 border-b border-gray-200 dark:border-gray-700" onDragOver={(e) => e.preventDefault()} onDrop={handleDropZone}>
                                    {addedFields.length === 0 ? (
                                        <div className="h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 px-4 text-center">
                                            <span className="hidden lg:inline">Kéo thả trường dữ liệu từ cột trái vào đây</span>
                                            <span className="lg:hidden text-sm">Nhấn "Thêm trường" để cấu hình dữ liệu</span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {addedFields.map((field, index) => (
                                                <div
                                                    key={field.id} draggable
                                                    onDragStart={(e) => e.dataTransfer.setData('sortIndex', index)}
                                                    onDragOver={(e) => { e.preventDefault(); setDragOverIndex(index); }}
                                                    onDragLeave={() => setDragOverIndex(null)}
                                                    onDrop={(e) => handleItemDrop(e, index)}
                                                    className={`relative bg-white dark:bg-gray-800 pt-4 pb-2 transition-all border-t-2 ${dragOverIndex === index ? 'border-blue-500 scale-[1.01] shadow-sm' : 'border-transparent'}`}
                                                >
                                                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2 cursor-move w-max">
                                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                                    </div>

                                                    <div className="flex flex-col md:flex-row items-start gap-4">
                                                        <div className="flex-1 w-full pointer-events-auto" onDragStart={(e) => e.preventDefault()} draggable>
                                                            {renderInteractiveInput(field)}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 mt-2 md:mt-1 shrink-0 w-full md:w-auto justify-end">
                                                            <div onClick={() => {
                                                                const newFields = [...addedFields];
                                                                newFields.splice(index + 1, 0, { ...field, id: `field_${Date.now()}` });
                                                                setAddedFields(newFields);
                                                            }} className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700"><Copy size={14} /></div>
                                                            <div onClick={() => {
                                                                setPendingField({ id: field.type, name: field.label });
                                                                setEditingId(field.id);
                                                                if (field.isFile) { formFile.setFieldsValue(field); setIsFileModalOpen(true); }
                                                                else { formStandard.setFieldsValue(field); setIsStandardModalOpen(true); }
                                                            }} className="w-8 h-8 flex items-center justify-center border border-blue-600 text-blue-600 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700"><Edit3 size={14} /></div>
                                                            <div onClick={() => setAddedFields(prev => prev.filter(f => f.id !== field.id))} className="w-8 h-8 flex items-center justify-center border border-red-500 text-red-500 rounded cursor-pointer hover:bg-red-50 dark:hover:bg-gray-700"><Trash2 size={14} /></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-8">
                                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-4">Các bước duyệt ký <span className="text-red-500">*</span></h4>
                                    <div className="space-y-4 mb-6">
                                        {approvalSteps.map((step, index) => (
                                            <div key={step.id} className="flex gap-3 md:gap-4">
                                                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold mt-1 shrink-0">{index + 1}</div>
                                                <div className="flex-1 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                                                    <div className="flex justify-between items-center p-3 border-b border-gray-200 dark:border-gray-700">
                                                        <span className="font-medium text-gray-800 dark:text-gray-100">{step.name}</span>
                                                        <div className="flex gap-2 text-blue-600">
                                                            <Clock size={16} className="cursor-pointer hover:opacity-70" />
                                                            <Edit3 size={16} className="cursor-pointer hover:opacity-70" onClick={() => handleEditStep(step)} />
                                                            <Trash2 size={16} className="cursor-pointer text-red-500 hover:opacity-70" onClick={() => setApprovalSteps(prev => prev.filter(s => s.id !== step.id))} />
                                                        </div>
                                                    </div>
                                                    <div className="p-3 bg-gray-50 dark:bg-gray-900/50 text-sm text-gray-600 dark:text-gray-300 flex flex-col gap-2">
                                                        {step.approvers && step.approvers.map((appr, idx) => (
                                                            <div key={idx} className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-200 dark:border-gray-700 pb-2 last:border-0 last:pb-0">
                                                                <span className="mb-1 sm:mb-0">{appr.role} | {appr.time}</span>
                                                                <span className="text-blue-600 flex items-center gap-1 cursor-pointer hover:opacity-70" onClick={() => handleEditStep(step)}>
                                                                    {appr.type} <Edit3 size={12} />
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="w-full h-12 border border-dashed border-blue-400 bg-blue-50/50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 font-medium rounded-lg cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/40 transition-colors"
                                        onClick={() => { setEditingStepId(null); formStep.resetFields(); setTempApprovers([]); setIsStepModalOpen(true); }}>
                                        Thêm bước trình ký
                                    </div>
                                </div>

                                <div className="mt-12 flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                                    <Button size="large" className="dark:bg-gray-700 dark:text-white dark:border-gray-600">Hủy</Button>
                                    <Button type="primary" size="large" className="bg-blue-600" onClick={() => { setCurrentStep(1) }}> Tiếp tục</Button>
                                </div>
                            </div>
                        </div>
                    </main>
                )}

                {currentStep === 1 && (
                    <DocumentSigningApplication />
                )}
            </div>

            {/* ========================================== */}
            {/* DRAWER CHO MOBILE (THAY THẾ SIDEBAR) */}
            {/* ========================================== */}
            <Drawer
                title={<span className="text-blue-600 font-semibold">Thêm trường dữ liệu</span>}
                placement="bottom"
                onClose={() => setIsMobileSidebarOpen(false)}
                open={isMobileSidebarOpen}
                height="80vh" // Vuốt lên chiếm 80% màn hình
                className="dark:bg-gray-800"
                styles={{ header: { borderBottom: '1px solid #374151' }, body: { padding: '16px' } }}
            >
                {/* Tái sử dụng giao diện Sidebar */}
                {renderSidebarContent()}
            </Drawer>

            {/* ========================================== */}
            {/* CÁC MODALS POPUPS DƯỚI NÀY GIỮ NGUYÊN ... */}
            {/* ========================================== */}
            <Modal title="Thiết lập thuộc tính" open={isStandardModalOpen} onCancel={() => setIsStandardModalOpen(false)} onOk={handleSaveStandardField} okText="Lưu" cancelText="Hủy" destroyOnClose>
                <Form form={formStandard} layout="vertical" className="mt-4">
                    <Form.Item name="label" label="Tên hiển thị *" rules={[{ required: true }]}><Input placeholder="VD: Ngày bắt đầu" size="large" /></Form.Item>
                    <Form.Item name="placeholder" label="Placeholder"><Input placeholder="Nhập chữ chìm gợi ý..." size="large" /></Form.Item>
                    <Form.Item name="required" valuePropName="checked"><div className="flex items-center justify-between"><span className="font-medium text-000000e0">Bắt buộc nhập</span><Switch /></div></Form.Item>
                </Form>
            </Modal>

            <Modal open={isFileModalOpen} onCancel={() => setIsFileModalOpen(false)} onOk={handleSaveFileField} okText="Thêm" cancelText="Hủy" closeIcon={false} width={600} destroyOnClose okButtonProps={{ className: "bg-blue-600" }}>
                <div className="flex items-center gap-2 font-medium mb-6 pb-4 border-b text-lg">
                    <Plus size={20} className="text-gray-500 rotate-45 cursor-pointer" onClick={() => setIsFileModalOpen(false)} /> Thêm trường dữ liệu Tài liệu
                </div>
                <Form form={formFile} layout="vertical">
                    <Form.Item name="label" label="Tên trường dữ liệu *" rules={[{ required: true }]}><Input placeholder="Nhập tên trường dữ liệu" size="large" /></Form.Item>
                    <Form.Item name="description" label="Giải thích thêm"><Input placeholder="Nhập nội dung giải thích" size="large" /></Form.Item>
                    <Form.Item label="Tài liệu mẫu *">
                        <Dragger className="bg-white border-blue-300">
                            <p className="ant-upload-drag-icon flex justify-center text-blue-500 mb-2"><Inbox size={32} /></p>
                            <p className="ant-upload-text text-blue-600 font-medium text-sm text-000000e0">File đính kèm</p>
                        </Dragger>
                    </Form.Item>
                    <Form.Item name="required" valuePropName="checked" initialValue={true}><div className="flex items-center gap-2"><Switch defaultChecked /><span className="font-medium text-000000e0">Bắt buộc</span></div></Form.Item>
                </Form>
            </Modal>

            <Modal open={isStepModalOpen} onCancel={() => setIsStepModalOpen(false)} footer={null} width={700} closeIcon={false} destroyOnClose>
                <div className="flex items-center gap-2 mb-6 pb-4 border-b font-medium text-lg">
                    <Plus size={20} className="text-gray-500 rotate-45 cursor-pointer" onClick={() => setIsStepModalOpen(false)} />
                    {editingStepId ? 'Chỉnh sửa bước duyệt ký' : 'Thêm bước duyệt ký'}
                </div>
                <Form form={formStep} layout="vertical" onFinish={(values) => {
                    if (editingStepId) setApprovalSteps(prev => prev.map(s => s.id === editingStepId ? { ...s, name: values.name, mode: values.mode, approvers: tempApprovers } : s));
                    else setApprovalSteps([...approvalSteps, { id: Date.now(), name: values.name, mode: values.mode, approvers: tempApprovers }]);
                    setIsStepModalOpen(false);
                }}>
                    <h4 className="font-semibold mb-4 text-sm text-gray-700">Thông tin cơ bản</h4>
                    <Form.Item name="name" label="Mô tả *" rules={[{ required: true }]}><Input placeholder="VD: Người ký chính" size="large" /></Form.Item>
                    <Form.Item name="mode" label="Chế độ" initialValue="Tuần tự"><Select size="large"><Option value="Tuần tự">Tuần tự</Option><Option value="Đồng thời">Đồng thời</Option></Select></Form.Item>

                    <div className="flex justify-between items-center mt-6 mb-4">
                        <h4 className="font-semibold text-sm text-gray-700">Thông tin người thực hiện</h4>
                        <Button type="primary" size="small" icon={<User size={14} />} className="bg-blue-600 text-xs flex items-center" onClick={() => handleOpenApproverModal(null)}>Thêm người ký</Button>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 overflow-x-auto">
                        <div className="min-w-[400px]">
                            <div className="grid grid-cols-3 text-xs font-semibold text-gray-500 border-b pb-2 mb-3">
                                <div>Chức vụ, thực hiện</div><div>Thời gian xử lý</div><div className="text-right">Hành động</div>
                            </div>
                            {tempApprovers.length === 0 ? <p className="text-xs text-center text-gray-400 py-2">Chưa có người ký</p> :
                                tempApprovers.map((appr, i) => (
                                    <div key={i} className="grid grid-cols-3 text-sm items-center mb-2 pb-2 border-b last:border-0 last:mb-0 last:pb-0">
                                        <div className="font-medium text-gray-700">{appr.role}</div>
                                        <div className="text-gray-600">{appr.time}</div>
                                        <div className="flex justify-end gap-2 text-blue-600">
                                            <Edit3 size={16} className="cursor-pointer hover:opacity-70" onClick={() => handleOpenApproverModal(i)} />
                                            <Trash2 size={16} className="cursor-pointer text-red-500 hover:opacity-70" onClick={() => setTempApprovers(prev => prev.filter((_, idx) => idx !== i))} />
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 border-t pt-4">
                        <Button onClick={() => setIsStepModalOpen(false)}>Hủy</Button>
                        <Button type="primary" htmlType="submit" className="bg-blue-600">Lưu</Button>
                    </div>
                </Form>
            </Modal>

            <Modal title={editingApproverIndex !== null ? "Chỉnh sửa người thực hiện" : "Thêm người thực hiện"} open={isAddApproverModalOpen} onCancel={() => setIsAddApproverModalOpen(false)} footer={null} width={500} destroyOnClose style={{ top: 20 }}>
                <Form form={formApprover} layout="vertical" className="mt-4" onFinish={handleSaveApprover}>
                    <Form.Item name="role" label="Chức danh thực hiện *" rules={[{ required: true }]}>
                        <Select placeholder="Chọn chức danh" size="large"><Option value="Trưởng phòng">Trưởng phòng</Option><Option value="Giám đốc">Giám đốc</Option></Select>
                    </Form.Item>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Form.Item name="time" label="Thời gian xử lý (SLA)" rules={[{ required: true }]}><Select placeholder="Chọn thời gian" size="large"><Option value="1 giờ">1 giờ</Option><Option value="4 giờ">4 giờ</Option></Select></Form.Item>
                        <Form.Item name="type" label="Loại chữ ký" rules={[{ required: true }]}><Select placeholder="Loại" size="large"><Option value="Nội bộ">Nội bộ</Option><Option value="Bên ngoài">Bên ngoài</Option></Select></Form.Item>
                    </div>
                    <div className="flex justify-end gap-3 border-t pt-4 mt-2">
                        <Button onClick={() => setIsAddApproverModalOpen(false)}>Hủy</Button>
                        <Button type="primary" htmlType="submit" className="bg-blue-600">Lưu</Button>
                    </div>
                </Form>
            </Modal>
        </Layout >
    );
}